import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Plus, Minus, X, ChefHat, Loader } from "lucide-react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency, getRestaurantSettings } from "@/utils/currency";
import {
  validatePhoneNumber,
  sanitizePhoneNumber,
  getPhoneValidationRules,
  COUNTRY_PHONE_CODES
} from "@/utils/phone";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import { useJsApiLoader } from "@react-google-maps/api";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentModal from "@/components/StripePaymentModal";
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function OnlineOrderPage() {
  const getVisibleCount = () => {
    if (window.innerWidth < 640) return 2; // mobile
    if (window.innerWidth < 768) return 3; // small tablet
    if (window.innerWidth < 1280) return 6; // tablet
    return 10; // desktop
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [startIndex, setStartIndex] = useState(0);
  const { restaurantSlug } = useParams();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [creditSlabs, setCreditSlabs] = useState([]);
  const hasCreditSlabs = creditSlabs.length > 0;

  // Customer info
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const categoryScrollRef = useRef(null);
  const [isMainCartLocked, setIsMainCartLocked] = useState(false);
  const [lockedCredit, setLockedCredit] = useState(0);
  const [extraCart, setExtraCart] = useState([]);
  const [remainingCredit, setRemainingCredit] = useState(0);
  const [showExtraItems, setShowExtraItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [selectedItemQuantity, setSelectedItemQuantity] = useState(1);

  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [deliveryProviders  , setDeliveryProviders] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
const [discounts, setDiscounts] = useState([]);
const [selectedDiscount, setSelectedDiscount] = useState(null);
const [selectedPaymentType, setSelectedPaymentType] = useState(null);
const [selectedVariant, setSelectedVariant] = useState(null);
const [paymentCompleted, setPaymentCompleted] = useState(false);
const [deliveryError, setDeliveryError] =
  useState("");
const [paymentTypes, setPaymentTypes] = useState({
  cash: true,
  card: false,
  upi: true,
  split: true
});

// Stripe Payment States
const [stripePromise, setStripePromise] = useState(null);
const [paymentProcessing, setPaymentProcessing] = useState(false);
const [paymentIntentData, setPaymentIntentData] = useState(null);
const [showStripeModal, setShowStripeModal] = useState(false);
const [restaurantSuspended, setRestaurantSuspended] =
  useState(false);
  const [autocomplete, setAutocomplete] = useState(null);

// Stripe will be initialized when payment intent is created with the API response publishable key
const inputRef = useRef(false);
const clickScrollingRef = useRef(false);
const settings = getRestaurantSettings();

const countryCode =
    COUNTRY_PHONE_CODES[settings.country]?.toLowerCase();
    const { isLoaded } = useJsApiLoader({
  googleMapsApiKey: "AIzaSyB-4F5PoN_g9thmi82CTIbB5uPT1EO7v2",
  libraries: ["places"],
});
  useEffect(() => {
    fetchRestaurant();
  }, [restaurantSlug]);

  useEffect(() => {
    if (restaurant) {
      fetchCategories();
      fetchMenuItems();
    }
  }, [restaurant]);

  useEffect(() => {
    if (restaurant) {
      fetchCreditSlabs();
      fetchDiscounts();
    }
  }, [restaurant]);
useEffect(() => {
  setDeliveryQuote(null);
}, [deliveryAddress]);
  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
      setStartIndex(0); // reset so UI never breaks
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {

  if (
    deliveryType === "delivery" &&
    deliveryAddress &&
    customerPhone &&
    customerName
  ) {

    const timeout = setTimeout(() => {
      getDeliveryQuote();
    }, 800);

    return () => clearTimeout(timeout);
  }

}, [
  deliveryAddress,
  customerPhone,
  customerName,
  deliveryType
]);
  const fetchCreditSlabs = async () => {
    try {
      const response = await axios.get(
        `${API}/public/credit-slabs?restaurant_id=${restaurant.id}`
      );

      setCreditSlabs(response.data);
    } catch (error) {
      console.error("Error fetching credit slabs:", error);
    }
  };
  const fetchDiscounts = async () => {
  try {

    const response = await axios.get(
      `${API}/public/discounts?restaurant_id=${restaurant.id}`
    );

    setDiscounts(response.data || []);

  } catch (error) {

    console.error("Error fetching discounts:", error);

  }
};
  const lockMainCart = () => {
    const credit = calculateCredit();
    setLockedCredit(credit);
    setRemainingCredit(credit);
    setIsMainCartLocked(true);
  };
const getDeliveryQuote = async () => {
  try {
    setDeliveryQuote(null);
    setDeliveryError("");
    setDeliveryLoading(true);

    const payload = {
      pickup_address: restaurant.address,
      dropoff_address: deliveryAddress,

      manifest_total_value: Math.round(finalGrandTotal() * 100),

      pickup_name: restaurant.name,
      dropoff_name: customerName,

      pickup_phone_number: "+" + (restaurant.phone || "+61400000000"),
      dropoff_phone_number: "+" + customerPhone
    };

    const token = localStorage.getItem("token");

    const response = await axios.post(
      `${API}/delivery/uber/quote`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    setDeliveryQuote(response.data.data);

} catch (error) {

  const errorData =
    error?.response?.data?.detail;

  console.log(
    "QUOTE ERROR =>",
    errorData || error.message
  );

  let errorMessage =
    "Unable to get delivery quote";

  // -----------------------------------
  // MAIN API MESSAGE
  // -----------------------------------

  if (errorData?.message) {
    errorMessage = errorData.message;
  }

  // -----------------------------------
  // ADDRESS OUTSIDE DELIVERY AREA
  // -----------------------------------

  if (
    errorData?.code ===
    "address_undeliverable"
  ) {

    errorMessage =
      "Delivery is not available for the selected address.";
  }

  // -----------------------------------
  // INVALID PHONE NUMBER
  // -----------------------------------

  if (
    errorData?.code ===
    "invalid_params"
  ) {

    if (
      errorData?.metadata?.dropoff_phone_number
    ) {

      errorMessage =
        "Please enter a valid customer phone number.";
    }
  }

  setDeliveryError(errorMessage);

  setDeliveryQuote(null);

} finally {

  setDeliveryLoading(false);
}
};
  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(
        `${API}/public/restaurant/${restaurantSlug}`
      );
      if (
        response.data?.status === "suspended"
      ) {
        setRestaurantSuspended(true);
        return;
      }
      setRestaurant(response.data);
      if (response.data.payment_types) {
        setPaymentTypes(response.data.payment_types);
        setDeliveryProviders(response.data.delivery_provider);
      }
       const restaurant = response.data;
      localStorage.setItem(
          "restaurant_settings",
          JSON.stringify({
            country: restaurant.country,
            currency: restaurant.currency,
            currency_symbol: restaurant.currency_symbol,
            locale: restaurant.locale || "en-US",
            timezone: restaurant.timezone || "UTC"
          })
        );
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      toast.error("Restaurant not found");
    }
  };
const availablePayments = Object.entries(paymentTypes)
  .filter(([key, enabled]) => enabled && key !== "split")
  .map(([key]) => key);
  
  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/public/categories?restaurant_id=${restaurant.id}`
      );
      setCategories(response.data);
      if (response.data.length > 0) {
        setSelectedCategory("all");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
const getItemDisplayPrice = (item) => {
  // Variant item
  if (
    item.pricing?.has_variants &&
    item.pricing?.variants?.length > 0
  ) {
    return item.pricing.variants[0]?.takeaway || 0;
  }

  // Normal item
  return (
    item.pricing?.takeaway ||
    item.price ||
    0
  );
};
  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(
        `${API}/public/menu/items?restaurant_id=${restaurant.id}&include_inherited=true`
      );
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.id === item.id);
    if (existing) {
      setCart(
        cart.map((c) =>
          c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const updateQuantity = (itemId, change) => {
    if (isMainCartLocked) {
      toast.error("Main order is locked. You can only add extra items now.");
      return;
    }

    setCart(
      cart
        .map((item) => {
          if (item.id === itemId) {
            const newQty = item.quantity + change;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (itemId) => {
    if (isMainCartLocked) {
      toast.error("Main order is locked. You can only add extra items now.");
      return;
    }
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const addExtraItem = (item) => {
    const price = item.pricing?.takeaway || item.price || 0;

   if (remainingCredit >= price) {
  const payable = 0;

  setRemainingCredit(remainingCredit - price);

  setExtraCart([...extraCart, {
    ...item,
    quantity: 1,
    payable,
    selected_modifiers: item.selected_modifiers || [],
    selected_addons: item.selected_addons || []
  }]);
    } else {
      // Partial credit
      const payable = price - remainingCredit;
      setRemainingCredit(0);
setExtraCart([
  ...extraCart,
  {
    ...item,
    quantity: 1,
    payable,
    selected_modifiers: item.selected_modifiers || [],
    selected_addons: item.selected_addons || []
  }
]);    }

    toast.success(`${item.name} added as extra item`);
  };

  const calculateCredit = () => {
    const total = calculateTotal();

    if (!creditSlabs || creditSlabs.length === 0) return 0;

    // Find matching slab
    const slab = creditSlabs.find(
      (s) => total >= s.min_value && total <= s.max_value
    );

    if (!slab) return 0;

    const credit = (total * slab.credit_percent) / 100;
    return credit;
  };
const getItemTotalPrice = (item) => {
const base =
  item.selected_price ||
  item.pricing?.takeaway ||
  item.price ||
  0;
  const modifiersPrice = (item.selected_modifiers || []).reduce((sum, group) => {
    return sum + group.options.reduce((s, opt) => s + (opt.price || 0), 0);
  }, 0);

  const addonsPrice = (item.selected_addons || []).reduce(
    (sum, addon) => sum + (addon.price || 0),
    0
  );

  return (base + modifiersPrice + addonsPrice) * item.quantity;
};
const calculateTotal = () => {
  return cart.reduce((sum, item) => sum + getItemTotalPrice(item), 0);
};
const calculateDiscountAmount = () => {

  if (!selectedDiscount) return 0;

  const subtotal = calculateTotal();

  if (selectedDiscount.type === "percentage") {

    return (subtotal * selectedDiscount.value) / 100;

  }

  if (selectedDiscount.type === "fixed") {

    return selectedDiscount.value;

  }

  return 0;
};
  const extraItemsTotal = () => {
    return extraCart.reduce((sum, item) => {
      const price = item.pricing?.takeaway || item.price || 0;
      return sum + price;
    }, 0);
  };

  const extraItemsPayable = () => {
    return extraCart.reduce((sum, item) => sum + item.payable, 0);
  };

  const totalCreditUsed = () => {
    return lockedCredit - remainingCredit;
  };

const finalGrandTotal = () => {

  const subtotal =
    calculateTotal() + extraItemsPayable();

  const discount =
    calculateDiscountAmount();

  return Math.max(subtotal - discount, 0);
};

  // Create payment intent for card payment
  const createPaymentIntent = async (orderPayload) => {
    try {
      setPaymentProcessing(true);
      
      const response = await axios.post(
        `${API}/payments/create-intent?restaurant_id=${restaurant.id}`,
        {
          order_type: orderPayload.order_type,
          customer_name: orderPayload.customer_name,
          customer_phone: orderPayload.customer_phone,
          customer_email: orderPayload.customer_email,
          customer_address: orderPayload.customer_address,
          items: orderPayload.items,
          discount: orderPayload.discount,
          delivery_quote: orderPayload.delivery_quote,
          delivery_type: orderPayload.delivery_type
        }
      );

      if (response.data.success) {
        setPaymentIntentData(response.data);
        // Initialize Stripe with publishable key from API response
        const stripe = await loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
        // Close payment selection modal
        setShowPaymentModal(false);
        // Show Stripe payment modal
        setShowStripeModal(true);
        return response.data;
      } else {
        toast.error("Failed to create payment intent");
        return null;
      }
    } catch (error) {
      console.error("Error creating payment intent:", error);
      toast.error(error.response?.data?.detail || "Failed to process payment");
      return null;
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Handle payment confirmation from Stripe modal
  const handlePaymentConfirm = async () => {
    if (!paymentIntentData) {
      toast.error("Payment intent not found");
      return;
    }

    const mergedItems = [
      ...cart.map((item) => ({
        item_id: item.id,
item_name: item.name,

variant_name:
  item.selected_variant?.name || null,        
  quantity: item.quantity,
price:
  item.selected_price ||
  item.pricing?.takeaway ||
  item.price ||
  0,        tax: item.tax || 0,
        modifiers: item.selected_modifiers || [],
        add_ons: item.selected_addons || [],
        is_extra_item: false
      })),
      ...extraCart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        quantity: 1,
        price: item.payable,
        tax: item.tax || 0,
        modifiers: item.selected_modifiers || [],
        add_ons: item.selected_addons || [],
        is_extra_item: true,
        credit_used:
          (item.pricing?.takeaway || item.price || 0) - item.payable
      })),
    ];

    const orderPayload = {
      order_type: "online",
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: customerEmail,
      customer_address: deliveryAddress,
      items: mergedItems,
      order_status: "pending",
      payment_method: selectedPaymentType === "card" ? "card" : "online",
      payment_status: "paid",
      delivery_quote: deliveryQuote,
      discount: selectedDiscount,
      delivery_type: deliveryType,
      stripe_payment_id: paymentIntentData.payment_intent_id
    };

    await submitOrder(orderPayload);
  };

  // Submit order to backend
  const submitOrder = async (orderPayload) => {
    try {
      const response = await axios.post(
        `${API}/orders/customer?restaurant_id=${restaurant.id}`,
        orderPayload
      );

      toast.success("Order placed successfully!");

      const uberTrackingUrl =
        response.data?.uber_delivery?.data?.uber_tracking_url;
      const orderId = response.data?.order?.id;
      
      setCart([]);
      setExtraCart([]);
      setShowCheckout(false);
      setShowPaymentModal(false);
      setShowStripeModal(false);
      setCustomerName("");
      setCustomerPhone("");
      setCustomerEmail("");
      setDeliveryAddress("");
      setPaymentIntentData(null);
      setSelectedPaymentType(null);

      if (uberTrackingUrl) {
        window.location.href = uberTrackingUrl;
      } else {
        navigate(`/track/${restaurantSlug}?order_id=${orderId}`);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error(error.response?.data?.detail || "Failed to place order. Please try again.");
      throw error;
    }
  };

  const handlePlaceOrder = async () => {
    if (!customerName || !customerPhone) {
      toast.error("Please enter your details");
      return;
    }
    if (
  !validatePhoneNumber(customerPhone)
) {

  toast.error(
    getPhoneValidationRules().message
  );

  return;
}
    if (
      deliveryType === "delivery" &&
      !deliveryAddress
    ) {
      toast.error("Please enter delivery address");
      return;
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    const mergedItems = [
      ...cart.map((item) => ({
        item_id: item.id,
item_name: item.name,
variant_id: item.selected_variant?.id || null,       
  quantity: item.quantity,
        price: item.pricing?.takeaway || item.price || 0,
        tax: item.tax || 0,
modifiers: (item.selected_modifiers || []).flatMap((group) =>
  (group.options || []).map((opt) => ({
    group_name: group.group,
    option_name: opt.name,
    option_price: opt.price || 0
  }))
),        add_ons: item.selected_addons || [],
        is_extra_item: false
      })),
      ...extraCart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        quantity: 1,
        price: item.payable,
        tax: item.tax || 0,
modifiers: (item.selected_modifiers || []).flatMap((group) =>
  (group.options || []).map((opt) => ({
    group_name: group.group,
    option_name: opt.name,
    option_price: opt.price || 0
  }))
),        add_ons: item.selected_addons || [],
        is_extra_item: true,
        credit_used:
          (item.pricing?.takeaway || item.price || 0) - item.payable
      })),
    ];

    const orderPayload = {
      order_type: "online",
      customer_name: customerName,
      customer_phone: customerPhone,
      customer_email: customerEmail,
      customer_address: deliveryAddress,
      items: mergedItems,
      order_status: "pending",
      payment_method: selectedPaymentType === "card" ? "card" : selectedPaymentType === "upi" ? "online" : "cash",
      payment_status:
        selectedPaymentType === "card" || selectedPaymentType === "upi"
          ? "pending"
          : "pending",
      delivery_quote: deliveryQuote,
      discount: selectedDiscount,
      delivery_type: deliveryType,
    };

    try {
      // Handle Stripe payment (card and online/upi)
      if (selectedPaymentType === "card" || selectedPaymentType === "upi") {
        const paymentIntent = await createPaymentIntent(orderPayload);
        if (!paymentIntent) {
          return;
        }
        // Stripe payment modal will handle the rest
        return;
      }

      // Handle cash payment
      await submitOrder(orderPayload);
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    }
  };

  const filteredItems = menuItems;

  const groupedItems = categories.reduce((acc, cat) => {
    const categoryItems = filteredItems.filter(
      (item) => item.category_id === cat.id
    );

    const subGrouped = categoryItems.reduce((subAcc, item) => {
      const key = item.sub_category_id || "no-sub";
      if (!subAcc[key]) subAcc[key] = [];
      subAcc[key].push(item);
      return subAcc;
    }, {});

    acc[cat.id] = subGrouped;
    return acc;
  }, {});

  const filteredCategories = categories.filter((cat) => {
    const subGroups = groupedItems[cat.id];
    return subGroups
      ? Object.values(subGroups).some((items) => items.length > 0)
      : false;
  });
useEffect(() => {

  const handleScroll = () => {

    const scrollPosition =
      window.scrollY + 220;

    let currentCategory = "all";

    for (const cat of filteredCategories) {

      const section = document.getElementById(
        `category-${cat.id}`
      );

      if (!section) continue;

      if (
        section.offsetTop <= scrollPosition
      ) {
        currentCategory = cat.id;
      }
    }

    setSelectedCategory(currentCategory);
  };

  window.addEventListener(
    "scroll",
    handleScroll,
    { passive: true }
  );

  handleScroll();

  return () =>
    window.removeEventListener(
      "scroll",
      handleScroll
    );

}, [filteredCategories]);
// useEffect(() => {
//   if (!isLoaded) return;
//   if (!inputRef.current) return;

//   const autocomplete = new window.google.maps.places.Autocomplete(
//     inputRef.current,
//     {
//       componentRestrictions: {
//         country: countryCode,
//       },
//     }
//   );

//   autocomplete.addListener("place_changed", () => {
//     const place = autocomplete.getPlace();

//     if (place?.formatted_address) {
//       setDeliveryAddress(place.formatted_address);
//     }
//   });
// }, [countryCode,deliveryAddress, isLoaded]);
const openItemModal = (item) => {
  setSelectedItem(item);

  setSelectedVariant(
    item.pricing?.has_variants
      ? item.pricing?.variants?.[0]
      : null
  );

  setSelectedModifiers({});
  setSelectedAddons([]);
  setSelectedItemQuantity(1);

  setShowModifierModal(true);
};
const handleModifierSelect = (groupName, option) => {
  setSelectedModifiers((prev) => ({
    ...prev,
    [groupName]: [option]
  }));
};
const handleMultiModifierSelect = (groupName, option) => {
  setSelectedModifiers((prev) => {
    const existing = prev[groupName] || [];

    const already = existing.find(o => o.name === option.name);

    if (already) {
      return {
        ...prev,
        [groupName]: existing.filter(o => o.name !== option.name)
      };
    }

    return {
      ...prev,
      [groupName]: [...existing, option]
    };
  });
};
const toggleAddon = (addon) => {
  setSelectedAddons((prev) => {
    const exists = prev.find(a => a.name === addon.name);

    if (exists) {
      return prev.filter(a => a.name !== addon.name);
    }

    return [...prev, addon];
  });
};
const isSameItem = (a, b) => {
  return (
    a.id === b.id &&
    JSON.stringify(a.selected_modifiers) === JSON.stringify(b.selected_modifiers) &&
    JSON.stringify(a.selected_addons) === JSON.stringify(b.selected_addons)
  );
};
const confirmAddToCart = () => {
  const modifiersArray = Object.entries(selectedModifiers).map(
    ([group, options]) => ({ group, options })
  );

const variantPrice =
  selectedVariant?.takeaway ||
  selectedItem.pricing?.takeaway ||
  selectedItem.price ||
  0;

const newItem = {
  ...selectedItem,

  selected_variant: selectedVariant,

  selected_price: variantPrice,

  quantity: selectedItemQuantity,

  selected_modifiers: modifiersArray,

  selected_addons: selectedAddons
};

  setCart(prev => {
    const existingIndex = prev.findIndex(item => isSameItem(item, newItem));

    if (existingIndex > -1) {
      const updated = [...prev];
      updated[existingIndex].quantity += 1;
      return updated;
    }

    return [...prev, newItem];
  });

  setShowModifierModal(false);
};
  useEffect(() => {
    setStartIndex(0);
  }, [filteredCategories.length, visibleCount]);

  const canGoLeft = startIndex > 0;
  const canGoRight = startIndex + visibleCount < filteredCategories.length;

  const visibleCategories = filteredCategories.slice(
    startIndex,
    startIndex + visibleCount
  );
if (restaurantSuspended) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md text-center p-6">
        <h1 className="text-2xl font-bold">
          Restaurant Unavailable
        </h1>

        <p className="mt-2 text-gray-500">
          This restaurant is currently unavailable.
        </p>
      </div>
    </div>
  );
}
  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Loading...</h1>
          <p className="text-gray-500">Please wait while we load the menu</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}

      <div className="bg-gradient-to-r from-red-600 to-red-700 shadow-lg sticky top-0 z-50">
        <div className="p-6 text-white max-w-6xl mx-auto relative">
          <div className="flex items-center gap-4">
            {/* <ChefHat className="w-10 h-10 text-white flex-shrink-0" /> */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold leading-tight">
                {restaurant.name}
              </h1>
              {restaurant.address && (
                <p className="text-gray-200 text-sm mt-1">
                  {restaurant.address}
                </p>
              )}
              <p className="text-red-100 text-sm">Online Ordering</p>
            </div>
          </div>
          {/* TRACK ORDER BUTTON - Top Right */}
          {/* <button
            onClick={() => navigate(`/track/${restaurantSlug}`)}
            className="absolute top-6 right-6 bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition shadow-md"
          >
            Track Order
          </button> */}
        </div>
<div className="sticky top-[96px] z-40 bg-white border-b">
  <div className="relative">

    {/* LEFT BUTTON - DESKTOP */}
    {/* LEFT BUTTON */}
{/* <button
  onClick={() =>
    categoryScrollRef.current?.scrollBy({
      left: -200,
      behavior: "smooth",
    })
  }
  className="absolute left-2 top-1/2 -translate-y-1/2 z-10
    flex items-center justify-center
    w-8 h-8 rounded-full shadow-md
    bg-red-600 text-white hover:bg-red-700 transition"
>
  <ChevronLeft size={18} />
</button> */}

    {/* SCROLLABLE CATEGORY BAR */}
<div
  ref={categoryScrollRef}
  className="
    flex items-center gap-3
    overflow-x-auto whitespace-nowrap
    scrollbar-hide
    px-12          {/* ← was px-4 md:px-16, now uniform px-12 to clear both arrows */}
    py-3
    scroll-smooth
  "
>

      {/* ALL BUTTON */}
      <button
onClick={() => {

  setSelectedCategory("all");

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}}
      >
        All
      </button>

      {/* CATEGORY LIST */}
      {filteredCategories.map((cat) => (
        <button
          id={`cat-btn-${cat.id}`}
          key={cat.id}
onClick={() => {

  setSelectedCategory(cat.id);

  const el = document.getElementById(
    `category-${cat.id}`
  );

  if (el) {

    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
}}
          className={`
            px-4 py-2 rounded-full text-sm font-medium
            flex-shrink-0 border transition-colors
            ${
              selectedCategory === cat.id
                ? "bg-red-600 text-white border-red-600"
                : "bg-white text-gray-600 border-gray-300 hover:border-red-600 hover:text-red-600"
            }
          `}
        >
          {cat.name}
        </button>
      ))}
    </div>

    {/* RIGHT BUTTON - DESKTOP */}
   {/* RIGHT BUTTON */}
{/* <button
  onClick={() =>
    categoryScrollRef.current?.scrollBy({
      left: 200,
      behavior: "smooth",
    })
  }
  className="absolute right-2 top-1/2 -translate-y-1/2 z-10
    flex items-center justify-center
    w-8 h-8 rounded-full shadow-md
    bg-red-600 text-white hover:bg-red-700 transition"
>
  <ChevronRight size={18} />
</button> */}
  </div>
</div>
      </div>
      {/* Cart Button */}
      <button
        onClick={() => setShowCart(!showCart)}
        className="fixed bottom-6 right-6 bg-red-600 text-white rounded-full p-4 shadow-2xl z-50 hover:bg-red-700 transition-all"
      >
        <ShoppingCart size={24} />
        {cart.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
            {cart.length}
          </span>
        )}
      </button>

      <div className="max-w-6xl mx-auto p-4  bg-[#f9f5f5]">
        <div>
          {Object.entries(groupedItems).map(([categoryId, subGroups]) => {
            const hasItems = Object.values(subGroups).some(
              (items) => items.length > 0
            );

            if (!hasItems) return null;

            return (
              <div key={categoryId}>
                {/* CATEGORY TITLE */}
                <h3
                  id={`category-${categoryId}`}
                  className="font-bold text-3xl text-gray-800 mb-6 scroll-mt-[190px]"
                >
                  {categories.find((cat) => cat.id === categoryId)?.name}
                </h3>

                {/* SUB CATEGORIES */}
                {Object.entries(subGroups).map(([subId, items]) => {
                  if (items.length === 0) return null;

                  return (
                    <div key={subId} className="mb-6">
                      {subId !== "no-sub" && (
                        <h4 className="text-xl font-semibold text-gray-700 mb-4 pl-1">
                          Sub Category
                        </h4>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {items.map((item) => (
                          <Card
                            key={item.id}
                            className="hover:shadow-lg transition-shadow border border-gray-200"
                          >
                            <CardContent className="p-4">
                              <h3 className="font-bold text-lg text-gray-800 mb-1">
                                {item.name}
                              </h3>

                              {item.description && (
                                <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                                  {item.description}
                                </p>
                              )}

{item.pricing?.has_variants ? (
  <div className="mb-3 space-y-1">
    {item.pricing?.variants?.map((variant) => (
      <div
        key={variant.id || variant.name}
        className="flex justify-between text-sm"
      >
        <span className="text-gray-700">
          {variant.name}
        </span>

        <span className="font-semibold text-red-600">
          {formatCurrency(variant.takeaway)}
        </span>
      </div>
    ))}
  </div>
) : (
  <p className="text-xl font-bold text-red-600 mb-3">
    {formatCurrency(getItemDisplayPrice(item))}
  </p>
)}

                              <Button onClick={() => openItemModal(item)}>
                                <Plus size={16} className="mr-2" />
                                Add
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="border-t border-gray-100 mt-6"></div>
              </div>
            );
          })}
        </div>
      </div>
          {showModifierModal && selectedItem && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto flex flex-col">
                
                {/* HEADER */}
                <div className="sticky top-0 bg-gradient-to-b from-red-50 to-white p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{selectedItem.name}</h2>
                      {selectedItem.description && (
                        <p className="text-sm text-gray-600 mt-1">{selectedItem.description}</p>
                      )}
                      <p className="text-lg font-bold text-red-600 mt-2">
                        {formatCurrency(selectedItem.pricing?.takeaway || selectedItem.price || 0)}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowModifierModal(false)}
                      className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>

                {/* SCROLLABLE CONTENT */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
                  {selectedItem.pricing?.has_variants && (
  <div className="space-y-3">
    <h3 className="text-lg font-semibold text-gray-800">
      Select Size
    </h3>

    <div className="space-y-2">
      {selectedItem.pricing?.variants?.map((variant) => (
        <label
          key={variant.id || variant.name}
          className={`
            flex items-center justify-between
            border rounded-lg p-3 cursor-pointer transition
            ${
              selectedVariant?.name === variant.name
                ? "border-red-600 bg-red-50"
                : "border-gray-200"
            }
          `}
        >
          <div className="flex items-center gap-3">
            <input
              type="radio"
              checked={
                selectedVariant?.name === variant.name
              }
              onChange={() => setSelectedVariant(variant)}
            />

            <span className="font-medium">
              {variant.name}
            </span>
          </div>

          <span className="font-semibold text-red-600">
            {formatCurrency(variant.takeaway)}
          </span>
        </label>
      ))}
    </div>
  </div>
)}
                  {/* MODIFIERS */}
                  {selectedItem.modifier_groups?.map((group) => (
                    <div key={group.name} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">{group.name}</h3>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {group.multi_select ? "Multiple" : "Single"}
                        </span>
                      </div>

                      <div className="space-y-2">
                        {group.options.map((opt) => (
                          <label
                            key={opt.name}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 cursor-pointer transition"
                          >
                            <input
                              type={group.multi_select ? "checkbox" : "radio"}
                              name={group.name}
                              className="w-5 h-5 cursor-pointer"
                              onChange={() =>
                                group.multi_select
                                  ? handleMultiModifierSelect(group.name, opt)
                                  : handleModifierSelect(group.name, opt)
                              }
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">{opt.name}</p>
                            </div>
                            <span className="text-red-600 font-semibold flex-shrink-0">
                              +{formatCurrency(opt.price)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* ADD ONS */}
                  {selectedItem.add_ons?.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-800">Add-ons</h3>
                      <div className="space-y-2">
                        {selectedItem.add_ons.map((addon) => (
                          <label
                            key={addon.name}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 cursor-pointer transition"
                          >
                            <input
                              type="checkbox"
                              className="w-5 h-5 cursor-pointer"
                              onChange={() => toggleAddon(addon)}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">{addon.name}</p>
                            </div>
                            <span className="text-red-600 font-semibold flex-shrink-0">
                              +{formatCurrency(addon.price)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* FOOTER - FIXED AT BOTTOM */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 sm:p-6 space-y-4">
                  
                  {/* QUANTITY SELECTOR */}
                  <div className="flex items-center justify-between bg-gray-100 rounded-lg p-3">
                    <span className="text-sm font-semibold text-gray-700">Quantity</span>
                    <div className="flex items-center gap-3 bg-white rounded-lg p-1">
                      <button
                        onClick={() => setSelectedItemQuantity(Math.max(1, selectedItemQuantity - 1))}
                        className="bg-gray-200 hover:bg-red-600 hover:text-white text-gray-700 rounded-full p-1.5 transition"
                      >
                        <Minus size={18} />
                      </button>
                      <span className="font-bold text-lg w-8 text-center">{selectedItemQuantity}</span>
                      <button
                        onClick={() => setSelectedItemQuantity(selectedItemQuantity + 1)}
                        className="bg-gray-200 hover:bg-red-600 hover:text-white text-gray-700 rounded-full p-1.5 transition"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setShowModifierModal(false)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmAddToCart}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
                    >
                      <Plus size={20} />
                      Add to Cart
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}
      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div
            className="fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Order Summary</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {cart.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No items added yet. Start adding delicious items to your
                  order.
                </p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 border-b pb-3"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold">{item.name}</h4>
                          {item.selected_variant && (
                            <p className="text-xs text-gray-500">
                              {item.selected_variant.name}
                            </p>
                          )}
                          <p className="text-sm text-gray-600">
                  
                            {formatCurrency(
                              item.pricing?.takeaway ||
                              item.price ||
                              0
                            )}{" "}
                            each
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="bg-gray-200 rounded-full p-1 hover:bg-gray-300"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="w-8 text-center font-semibold">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="bg-gray-200 rounded-full p-1 hover:bg-gray-300"
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-600 hover:text-red-700 ml-2"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mb-6 space-y-2">
                    {/* Subtotal */}
                    <div className="flex justify-between text-lg">
                      <span>Subtotal:</span>
                      <span className="font-semibold">
                        {formatCurrency(calculateTotal())}
                      </span>
                    </div>

                    {/* <div className="flex justify-between text-xl font-bold pt-2 border-t">
                      <span>Grand Total:</span>
                      <span className="text-red-600">{formatCurrency(calculateTotal())}</span>
                    </div> */}
                  </div>
                  {/* DISCOUNTS */}

                  {discounts.length > 0 && (
                    <div className="border-t pt-4 mb-4">

                      <h3 className="font-semibold mb-3">
                        Apply Discount
                      </h3>

                      <div className="space-y-3">
                        {discounts.map((discount) => {
                          const isApplied =
                            selectedDiscount?.id === discount.id;

                          return (
                            <div
                              key={discount.id}
                              className={`w-full border rounded-lg p-4 transition ${
                                isApplied
                                  ? "border-red-600 bg-red-50"
                                  : "border-gray-200 bg-white"
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {discount.name}
                                  </p>
                                  <p className="text-sm text-gray-500 capitalize">
                                    {discount.type}
                                  </p>
                                </div>

                                <div className="flex items-center gap-3">
                                  <div className="font-bold text-red-600 text-right">
                                    {discount.type === "percentage"
                                      ? `${discount.value}% OFF`
                                      : formatCurrency(discount.value)}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedDiscount(discount)
                                    }
                                    disabled={isApplied}
                                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                                      isApplied
                                        ? "bg-slate-200 text-slate-700 cursor-not-allowed"
                                        : "bg-red-600 text-white hover:bg-red-700"
                                    }`}
                                  >
                                    {isApplied ? "Applied" : "Apply"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    <div className="flex justify-between text-xl font-bold border-t pt-3">
                      <span>Total Amount to Pay:</span>
                      <span className="text-red-700">
                        {formatCurrency(finalGrandTotal())}
                      </span>
                    </div>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      if (hasCreditSlabs) {
                        lockMainCart();
                        setShowExtraItems(true);
                      } else {
                        setShowCheckout(true);
                      }
                      setShowCart(false);
                    }}
                    className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition-all"
                  >
                    Proceed to Checkout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showExtraItems && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div
            className="fixed right-0 top-0 h-full w-full md:w-96 bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Redeem Your Credit</h2>
                <button
                  onClick={() => setShowExtraItems(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-green-600 text-sm font-medium mb-4">
                🎁 Apply your credit to add extras — see your total drop
                instantly!
              </p>

              <p className="text-green-700 font-semibold mb-2">
                Remaining Credit: {formatCurrency(remainingCredit)}
              </p>
              <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50 mt-4">
                {menuItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-center py-2 border-b"
                  >
                    <div>
                      <h4 className="font-semibold">{item.name}</h4>
                      <p className="text-gray-500">
                        {formatCurrency(item.pricing?.takeaway || item.price)}
                      </p>
                    </div>
                    <button
                      className="bg-red-600 text-white px-3 py-1 rounded"
                      onClick={() => addExtraItem(item)}
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>

              {/* Extra Items Summary */}
              <h3 className="text-xl font-bold mt-4">Extra Items Summary</h3>
              {extraCart.length === 0 ? (
                <p className="text-gray-500">No extra items added.</p>
              ) : (
                <div className="space-y-2">
                  {extraCart.map((item) => (
                    <div key={item.id} className="flex justify-between py-1">
                      <span>{item.name}</span>
                      <span
                        className={
                          item.payable === 0 ? "text-green-600" : "text-red-600"
                        }
                      >
                        {item.payable === 0
                          ? "FREE (Credit Applied)"
                          : `${formatCurrency(item.payable)}`}
                      </span>
                    </div>
                  ))}

                  {/* Full totals */}
                  <div className="border-t pt-3 mt-4 space-y-2 text-lg">
                    <div className="flex justify-between">
                      <span>Original Bill Amount:</span>
                      <span className="font-semibold">
                        {formatCurrency(calculateTotal())}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span>Credit Earned from Order:</span>
                      <span className="font-semibold">
                        {formatCurrency(lockedCredit)}
                      </span>
                    </div>
                    {/* <div className="flex justify-between text-blue-700">
                      <span>Credit Redeemed:</span>
                      <span className="font-semibold">{formatCurrency(totalCreditUsed())}</span>
                    </div> */}
                    <div className="flex justify-between">
                      <span>Extra Items Value:</span>
                      <span>{formatCurrency(extraItemsTotal())}</span>
                    </div>
                    <div className="flex justify-between text-red-600 font-bold">
                      <span>Payable for Extra Items:</span>
                      <span>{formatCurrency(extraItemsPayable())}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold border-t pt-3">
                      <span>Total Amount to Pay:</span>
                      <span className="text-red-700">
                        {formatCurrency(finalGrandTotal())}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="w-full mt-6 bg-green-600 text-white py-3 rounded font-bold"
                onClick={() => {
                  setShowExtraItems(false);
                  setShowCheckout(true);
                }}
              >
                Continue to Checkout
              </button>

              <button
                className="w-full mt-2 bg-gray-300 py-3 rounded"
                onClick={() => setShowExtraItems(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Contact Information</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  required
                />
              </div>

<div>
  <label className="block text-sm font-semibold mb-2">
    Phone Number *
  </label>

  <input
    type="tel"
    value={customerPhone}
    onChange={(e) => {
      setCustomerPhone(
        sanitizePhoneNumber(e.target.value)
      );
    }}
    placeholder={
      getPhoneValidationRules().placeholder
    }
    className="w-full border border-gray-300 rounded-lg px-4 py-2"
    required
  />
</div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Delivery Type
                </label>

                <div className="flex gap-4">

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="pickup"
                      checked={deliveryType === "pickup"}
                      onChange={(e) => setDeliveryType(e.target.value)}
                    />

                    Pickup
                  </label>
                  {(deliveryProviders==="internal"||deliveryProviders==="uber") && (
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="delivery"
                      checked={deliveryType === "delivery"}
                      onChange={(e) => setDeliveryType(e.target.value)}
                    />

                    Delivery
                  </label>
                  )}

                </div>
              </div>
{deliveryType === "delivery" && (
  <div>
    <label className="block text-sm font-semibold mb-2">
      Delivery Address *
    </label>

    <input
      ref={inputRef}
      type="text"
      value={deliveryAddress}
      onChange={(e) => setDeliveryAddress(e.target.value)}
      placeholder="Search or enter address manually"
      className="w-full border border-gray-300 rounded-lg px-4 py-2"
    />
  </div>
)}
                {deliveryType === "delivery" && deliveryLoading && (
                  <div className="border rounded p-3 mt-4 bg-yellow-50 text-yellow-800">
                    <div className="flex items-center gap-2">
                      <Loader className="animate-spin" size={18} />
                      <span>Getting delivery fare...</span>
                    </div>
                  </div>
                )}
                {deliveryType === "delivery" &&
                  deliveryLoading && (

                  <div className="border rounded p-3 mt-4 bg-yellow-50 text-yellow-800">
                    <span>
                      Delivery fare is being fetched. Please wait a moment.
                    </span>
                  </div>
                )}
                {deliveryType === "delivery" && deliveryQuote && (
                  <div className="border rounded p-3 mt-4 bg-green-50">

                    <div className="flex justify-between">
                      <span>Delivery Fee</span>
                      <span>
                        {formatCurrency(deliveryQuote.fee / 100)}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span>Estimated Time</span>
                      <span>
                        {Math.round(deliveryQuote.duration / 60)} mins
                      </span>
                    </div>

                    <div className="flex justify-between font-bold border-t mt-2 pt-2">
                      <span>Total Payable</span>
                      <span>
                        {formatCurrency(
                          finalGrandTotal() +
                          (
                            deliveryType === "delivery"
                              ? deliveryQuote.fee / 100
                              : 0
                          )
                        )}
                      </span>
                    </div>

                  </div>
                )}
                {deliveryType === "delivery" &&
                  deliveryError && (

                  <div className="border border-red-200 bg-red-50 rounded p-3 mt-4">

                    <p className="text-sm text-red-700 font-medium">
                      {deliveryError}
                    </p>

                  </div>
                )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCheckout(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowPaymentModal(true)}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {/* PAYMENT MODAL */}

      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">

          <div className="bg-white rounded-xl w-full max-w-md p-6">

            <h2 className="text-2xl font-bold mb-4">
              Select Payment Method
            </h2>

            <div className="space-y-3">

              {availablePayments.map((type) => (

                <button
                  key={type}
                  onClick={() => setSelectedPaymentType(type)}
                  className={`
                    w-full border rounded-lg p-4 text-left capitalize transition
                    ${
                      selectedPaymentType === type
                        ? "border-red-600 bg-red-50"
                        : "border-gray-200"
                    }
                  `}
                >
                  {type === "upi" ? "Online" : type}
                </button>

              ))}

            </div>


            {selectedPaymentType && (

              <div className="mt-6 border-t pt-4">

                <div className="mb-4">

                  <p className="font-semibold">
                    Selected:
                    {" "}
                    <span className="capitalize">
                      {selectedPaymentType === "upi" ? "Online" : selectedPaymentType}
                    </span>
                  </p>


                </div>

                <button
                  disabled={isPlacingOrder}
                  onClick={async () => {
                    if (isPlacingOrder) return;

                    try {
                      setIsPlacingOrder(true);

                      if (
                        selectedPaymentType === "card" ||
                        selectedPaymentType === "upi"
                      ) {
                        await handlePlaceOrder();
                      } else {
                        setPaymentCompleted(true);
                        toast.success("Proceeding with order...");
                        setShowPaymentModal(false);
                        await handlePlaceOrder();
                      }
                    } catch (err) {
                      console.error(err);
                    } finally {
                      setIsPlacingOrder(false);
                    }
                  }}
                  className={`w-full py-3 rounded-lg font-bold text-white ${
                    isPlacingOrder
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isPlacingOrder ? "Processing..." : "Place Order"}
                </button>

              </div>

            )}

            <button
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedPaymentType(null);
              }}
              className="w-full mt-3 bg-gray-200 py-3 rounded-lg"
            >
              Cancel
            </button>

          </div>

        </div>
      )}

      {/* STRIPE PAYMENT MODAL */}
      {stripePromise && showStripeModal && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: paymentIntentData?.client_secret,
          }}
        >    
          <StripePaymentModal
            isOpen={showStripeModal}
            onClose={() => {
              setShowStripeModal(false);
              setPaymentIntentData(null);
            }}
            // onPaymentConfirm={handlePaymentConfirm}
            paymentIntent={paymentIntentData}
            loading={paymentProcessing}
            customerName={customerName}
            restaurant={restaurant}
            customerEmail={customerEmail}
            customerPhone={customerPhone}
          />
        </Elements>
      )}

    </div>
  );
}
